import {
  DefectCatalogItem,
  DefectMeasurementSlug,
  DefectSlug,
  DefectVisualizationRule,
} from '../contracts/availableLayers';
import type { DefectRule } from '../contracts/defectRulesets';
import {
  buildRulesFromColorScheme,
  type DefectBandSlug,
} from '../map/buildRulesFromColorScheme';
import {
  filterRulesToWorstCondition,
  isWorstConditionRuleset,
} from './worstConditionOnly';

export const VISUALIZATION_THRESHOLD_COUNT = 4;

export interface ActiveLayerVisualizationSettings {
  readonly measurementSlug: DefectMeasurementSlug;
  readonly ruleName: string;
  readonly ruleDescription: string;
  readonly colorLow: string;
  readonly colorHigh: string;
  readonly thresholds: readonly number[];
  readonly highlightValue?: string;
  readonly savedAt?: string;
}

export function normalizeThresholds(
  thresholds: readonly number[],
): readonly number[] {
  const values = thresholds.slice(0, VISUALIZATION_THRESHOLD_COUNT).map((value) =>
    Number.isFinite(value) ? value : 0,
  );

  while (values.length < VISUALIZATION_THRESHOLD_COUNT) {
    const last = values.length > 0 ? values[values.length - 1] : 0;
    values.push(last + 1);
  }

  return values;
}

export function seedSettingsFromVisualizationRule(
  rule: DefectVisualizationRule,
): ActiveLayerVisualizationSettings {
  return {
    measurementSlug: rule.slug,
    ruleName: rule.name,
    ruleDescription: rule.description,
    colorLow: rule.colorScheme.min,
    colorHigh: rule.colorScheme.max,
    thresholds: normalizeThresholds(rule.thresholds),
  };
}

export function findCatalogMeasurement(
  defects: readonly DefectCatalogItem[],
  defectSlug: DefectSlug,
  measurementSlug: DefectMeasurementSlug,
): DefectVisualizationRule | undefined {
  const defect = defects.find((item) => item.slug === defectSlug);
  return defect?.measurements.find((measurement) => measurement.slug === measurementSlug);
}

function settingsMatchMeasurement(
  settings: ActiveLayerVisualizationSettings | null | undefined,
  measurementSlug: DefectMeasurementSlug,
): settings is ActiveLayerVisualizationSettings {
  return settings != null && settings.measurementSlug === measurementSlug;
}

export function settingsToDefectRules(
  settings: ActiveLayerVisualizationSettings,
  bandLabels?: Partial<Record<DefectBandSlug, string>>,
  options?: {
    readonly rulesetSlug?: string;
  },
): readonly DefectRule[] {
  const rules = buildRulesFromColorScheme(
    {
      min: settings.colorLow,
      max: settings.colorHigh,
    },
    bandLabels,
  );

  if (isWorstConditionRuleset(options?.rulesetSlug)) {
    return filterRulesToWorstCondition(rules);
  }

  return rules;
}

export function resolveInitialVisualizationSettings(input: {
  readonly catalogRule: DefectVisualizationRule | undefined;
  readonly apiSettings: ActiveLayerVisualizationSettings | null;
  readonly savedSettings: ActiveLayerVisualizationSettings | null;
  readonly measurementSlug: DefectMeasurementSlug;
}): ActiveLayerVisualizationSettings | null {
  if (settingsMatchMeasurement(input.apiSettings, input.measurementSlug)) {
    return input.apiSettings;
  }
  if (settingsMatchMeasurement(input.savedSettings, input.measurementSlug)) {
    return input.savedSettings;
  }
  if (input.catalogRule) {
    return seedSettingsFromVisualizationRule(input.catalogRule);
  }
  return null;
}

export function isValidHexColor(value: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(value.trim());
}
